package top.wcpe.allincore.dsl.compiler.ast

import top.wcpe.allincore.dsl.compiler.source.SourcePosition
import top.wcpe.allincore.dsl.compiler.type.IrType

/**
 * 语句 AST。
 */
sealed interface StmtAst : AstNode {

    /** `lhs = rhs` 赋值语句。 */
    data class AssignStmt(
        val lhs: LValueAst,
        val rhs: ExprAst,
        override val position: SourcePosition,
    ) : StmtAst {
        override val range
            get() = lhs.range.merge(rhs.range)
    }

    /**
     * 表达式语句：把一个调用表达式当语句执行，丢弃返回值。
     *
     * 仅允许 [ExprAst.CallExpr] 或 [ExprAst.MemberCall] 作为 [call]；由解析器负责校验。
     */
    data class ExprStmt(
        val call: ExprAst,
        override val position: SourcePosition,
    ) : StmtAst {
        override val range
            get() = call.range
    }

    // ===== C1 新增语句 =====

    /**
     * `var NAME = expr` 局部变量声明（块级作用域）。
     *
     * [inferredType] 为 D2-a 引入的类型推断结果占位字段；当前仅 TypeInferencer
     * 主动填充，runtime/serializer 暂不消费。默认 null 保持向后兼容。
     */
    data class VarDeclStmt(
        val name: String,
        val initializer: ExprAst,
        override val position: SourcePosition,
        val inferredType: IrType? = null,
        /** M5-c：用户书写的显式类型标注 `var x: T = ...`；未标注时为 null。 */
        val typeAnnotation: TypeAst? = null,
    ) : StmtAst {
        override val range
            get() = position.range.merge(initializer.range)
    }

    /**
     * `if (cond) { thenBody } else { elseBody }`。
     *
     * `else if` 由解析器递归展开为 IfStmt 嵌入 elseBody（单条 IfStmt）。
     * 没有 else 分支时 [elseBody] 为空列表。
     */
    data class IfStmt(
        val cond: ExprAst,
        val thenBody: List<StmtAst>,
        val elseBody: List<StmtAst>,
        override val position: SourcePosition,
    ) : StmtAst

    /** `while (cond) { body }`。 */
    data class WhileStmt(
        val cond: ExprAst,
        val body: List<StmtAst>,
        override val position: SourcePosition,
    ) : StmtAst

    /** `break`，仅在 while 内合法。 */
    data class BreakStmt(
        override val position: SourcePosition,
    ) : StmtAst

    /** `continue`，仅在 while 内合法。 */
    data class ContinueStmt(
        override val position: SourcePosition,
    ) : StmtAst

    /** `return` 或 `return expr`，作用域为最近的 action / callback 体。 */
    data class ReturnStmt(
        val value: ExprAst?,
        override val position: SourcePosition,
    ) : StmtAst {
        override val range
            get() = value?.let { position.range.merge(it.range) } ?: position.range
    }

    /**
     * `try { tryBody } catch (name) { catchBody } [finally { finallyBody }]`。
     *
     * 仅捕获运行时抛出的 DslRuntimeError；finallyBody 为空表示未声明 finally。
     */
    data class TryStmt(
        val tryBody: List<StmtAst>,
        val catchName: String,
        val catchBody: List<StmtAst>,
        val finallyBody: List<StmtAst>,
        override val position: SourcePosition,
    ) : StmtAst

    // ===== C2 新增语句 =====

    /** `for (NAME in expr) { body }` 迭代循环（参与 loopDepth）。 */
    data class ForInStmt(
        val varName: String,
        val iterable: ExprAst,
        val body: List<StmtAst>,
        override val position: SourcePosition,
    ) : StmtAst

    /**
     * 语句形态的 when：与 [ExprAst.WhenExpr] 同结构，仅在解释器中按语句语义执行
     * （丢弃分支体最后表达式的值）。
     */
    data class WhenStmt(
        val whenExpr: ExprAst.WhenExpr,
        override val position: SourcePosition,
    ) : StmtAst

    // ===== C4 新增语句：局部 fun 声明 =====

    /**
     * `fun NAME(params) { body }` 局部函数声明（块级作用域）。
     *
     * 与 lambda 的"按值闭包"不同，局部 fun 通过共享当前 EvalContext 的 var 帧栈
     * 实现"按引用闭包"——其调用语义详见 ui-runtime LocalFunValue。
     */
    data class FunDeclStmt(
        val decl: FunDecl,
        override val position: SourcePosition,
    ) : StmtAst

    /**
     * `batch { stmts }` 合批写入语句。
     *
     * 运行时在 BatchScope enter/exit 之间执行 body 内所有语句，使响应式写入
     * （state.x = ... / list.add / map.put 等）合并为一次订阅者通知。
     *
     * 内部禁止出现 await / suspend 调用——会引入异步边界，破坏合批的同步语义。
     */
    data class BatchStmt(
        val body: List<StmtAst>,
        override val position: SourcePosition,
    ) : StmtAst
}

/** 请求回调种类。 */
enum class RequestCallbackKind { PENDING, SUCCESS, FAIL, TIMEOUT }

/**
 * 请求回调块。
 *
 * - [paramName] 仅在 SUCCESS/FAIL 出现，PENDING/TIMEOUT 为 null
 */
data class RequestCallback(
    val kind: RequestCallbackKind,
    val paramName: String?,
    val body: List<StmtAst>,
    override val position: SourcePosition,
) : AstNode

/**
 * 赋值左值。MVP 仅支持 `state.<name>`。
 */
sealed interface LValueAst : AstNode {

    /** `state.<name>`。 */
    data class StateLValue(
        val name: String,
        override val position: SourcePosition,
    ) : LValueAst

    /** `session.<name>`。 */
    data class SessionLValue(
        val name: String,
        override val position: SourcePosition,
    ) : LValueAst

    /** `persist.<name>`。 */
    data class PersistLValue(
        val name: String,
        override val position: SourcePosition,
    ) : LValueAst

    /** 局部 `var` 引用作为左值（C1 新增）。 */
    data class LocalLValue(
        val name: String,
        override val position: SourcePosition,
    ) : LValueAst

    /**
     * M6-a：索引位置左值 `target[index] = rhs`。
     *
     * - List：index 为整数，越界写入抛运行期错误
     * - Map：index 为字符串；不存在 key 时直接插入
     */
    data class IndexLValue(
        val target: ExprAst,
        val index: ExprAst,
        override val position: SourcePosition,
    ) : LValueAst
}
